import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, useViewMode } from '../App';
import { walletApi, notificationApi } from '../lib/api';
import { formatCurrency, formatRelativeTime, getTransactionStyle } from '../lib/utils';
import BottomNav from './BottomNav';
import { 
  Home, ArrowLeftRight, TrendingUp, CreditCard, Settings, 
  Bell, LogOut, ArrowDownLeft, ArrowUpRight, Eye, EyeOff,
  Send, Wallet, Receipt, ChevronRight, Smartphone, Monitor, LayoutGrid,
  Scissors, Heart, Crown, Briefcase
} from 'lucide-react';

const BOTTOM_NAV_ROUTES = ['/home', '/payments', '/earn', '/cards', '/more', '/clips', '/tipping', '/subscriptions', '/marketplace'];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_confusing-preview/artifacts/gu4q9ocf_trans_white_logo.png";

const AppLayout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { viewMode, setViewMode, isForcedMobile, isForcedDesktop } = useViewMode();
  const [wallet, setWallet] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [hideBalance, setHideBalance] = useState(false);
  const [naturalDesktop, setNaturalDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => setNaturalDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine actual display mode
  const isDesktop = isForcedDesktop ? true : isForcedMobile ? false : naturalDesktop;
  const showBottomNav = BOTTOM_NAV_ROUTES.some(r => location.pathname === r || (r !== '/home' && location.pathname.startsWith(r)));

  useEffect(() => {
    if (isDesktop) {
      fetchSidebarData();
    }
  }, [isDesktop]);

  const fetchSidebarData = async () => {
    try {
      const [walletRes, txRes, notifRes] = await Promise.all([
        walletApi.getWallet(),
        walletApi.getTransactions({ limit: 5 }),
        notificationApi.getAll(5),
      ]);
      setWallet(walletRes.data);
      setRecentTransactions(txRes.data);
      setNotifications(notifRes.data);
    } catch (error) {
      console.error('Failed to fetch sidebar data');
    }
  };

  const navItems = [
    { path: '/home', icon: Home, label: 'Dashboard' },
    { path: '/payments', icon: ArrowLeftRight, label: 'Payments' },
    { path: '/earn', icon: TrendingUp, label: 'Earn' },
    { path: '/clips', icon: Scissors, label: 'Clips' },
    { path: '/tipping', icon: Heart, label: 'Tips' },
    { path: '/subscriptions', icon: Crown, label: 'Subscriptions' },
    { path: '/marketplace', icon: Briefcase, label: 'Marketplace' },
    { path: '/cards', icon: CreditCard, label: 'Cards' },
    { path: '/more', icon: Settings, label: 'Settings' },
  ];

  const quickActions = [
    { path: '/send', icon: Send, label: 'Send', color: 'bg-primary' },
    { path: '/receive', icon: ArrowDownLeft, label: 'Receive', color: 'bg-green-500' },
    { path: '/withdraw', icon: Wallet, label: 'Withdraw', color: 'bg-blue-500' },
    { path: '/payments', icon: Receipt, label: 'Pay Bills', color: 'bg-purple-500' },
  ];

  const isActive = (path) => {
    if (path === '/home') return location.pathname === '/home';
    return location.pathname.startsWith(path);
  };

  // Mobile Layout
  if (!isDesktop) {
    return (
      <div className="min-h-screen bg-background">
        {isForcedMobile ? (
          <div className="max-w-[430px] mx-auto relative forced-mobile">
            {children}
            {showBottomNav && <BottomNav />}
          </div>
        ) : (
          <>
            {children}
            {showBottomNav && <BottomNav />}
          </>
        )}
        <ViewToggleFAB viewMode={viewMode} setViewMode={setViewMode} />
      </div>
    );
  }

  // Desktop Layout - Dashboard style with sidebars
  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Sidebar - Navigation */}
      <aside className="w-64 bg-card border-r border-white/10 flex flex-col fixed left-0 top-0 h-screen z-40">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <Link to="/home" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="LekeeWallet" className="h-10 w-auto" />
          </Link>
        </div>

        {/* User Profile */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            {user?.avatar_url ? (
              <img 
                src={user.avatar_url} 
                alt={user.full_name} 
                className="w-12 h-12 rounded-full object-cover border-2 border-white/20"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-lg font-bold">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.lekeewallet_id}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(({ path, icon: Icon, label }) => (
            <Link
              key={path}
              to={path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive(path)
                  ? 'bg-primary text-white shadow-lg shadow-primary/30'
                  : 'text-muted-foreground hover:text-white hover:bg-white/5'
              }`}
              data-testid={`desktop-nav-${label.toLowerCase()}`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{label}</span>
            </Link>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={() => { logout(); window.location.href = '/login'; }}
            className="flex items-center gap-3 w-full px-4 py-3 text-red-500 hover:bg-white/5 rounded-xl transition-colors"
            data-testid="desktop-logout"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Log Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 mr-80">
        {/* Top Bar */}
        <header className="sticky top-0 bg-background/95 backdrop-blur-sm z-30 border-b border-white/10 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {navItems.find(n => isActive(n.path))?.label || 'LekeeWallet'}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/notifications" 
                className="relative p-2 hover:bg-white/5 rounded-xl transition-colors"
                data-testid="desktop-notifications"
              >
                <Bell className="w-5 h-5" />
                {notifications.filter(n => !n.read).length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full text-xs flex items-center justify-center">
                    {notifications.filter(n => !n.read).length}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-8">
          {children}
        </div>
      </main>

      {/* Right Sidebar - Stats & Activity */}
      <aside className="w-80 bg-card border-l border-white/10 fixed right-0 top-0 h-screen overflow-y-auto z-40">
        {/* Balance Card */}
        <div className="p-6 border-b border-white/10">
          <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl p-5 border border-primary/20">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Total Balance</span>
              <button 
                onClick={() => setHideBalance(!hideBalance)}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              >
                {hideBalance ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-3xl font-bold balance-text mb-4" data-testid="sidebar-balance">
              {hideBalance ? '₦•••••••' : formatCurrency(wallet?.available_balance)}
            </p>
            <div className="flex items-center gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Available</span>
                <p className="text-green-500 font-medium">
                  {hideBalance ? '•••' : formatCurrency(wallet?.available_balance)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Pending</span>
                <p className="text-yellow-500 font-medium">
                  {hideBalance ? '•••' : formatCurrency(wallet?.pending_balance)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="p-6 border-b border-white/10">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map(({ path, icon: Icon, label, color }) => (
              <Link
                key={path}
                to={path}
                className="flex flex-col items-center gap-2 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                data-testid={`quick-action-${label.toLowerCase()}`}
              >
                <div className={`w-10 h-10 ${color} rounded-full flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-xs font-medium">{label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="p-6 border-b border-white/10">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Quick Stats</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <ArrowDownLeft className="w-4 h-4 text-green-500" />
                </div>
                <span className="text-sm">Money In (30d)</span>
              </div>
              <span className="font-medium text-green-500">
                {hideBalance ? '•••' : formatCurrency(recentTransactions.filter(t => t.direction === 'in').reduce((sum, t) => sum + t.amount, 0))}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4" />
                </div>
                <span className="text-sm">Money Out (30d)</span>
              </div>
              <span className="font-medium">
                {hideBalance ? '•••' : formatCurrency(recentTransactions.filter(t => t.direction === 'out').reduce((sum, t) => sum + t.amount, 0))}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm">KYC Tier</span>
              </div>
              <span className="font-medium">Tier {wallet?.kyc_tier || 1}</span>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Recent Activity</h3>
            <Link to="/transactions" className="text-xs text-primary hover:underline flex items-center gap-1">
              View All <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {recentTransactions.slice(0, 5).map((tx) => {
              const style = getTransactionStyle(tx.type, tx.direction);
              const Icon = tx.direction === 'in' ? ArrowDownLeft : ArrowUpRight;
              
              return (
                <Link
                  key={tx.transaction_id}
                  to={`/transactions/${tx.transaction_id}`}
                  className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                >
                  <div className={`w-8 h-8 rounded-full ${style.bgColor} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${style.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{tx.counterparty_name}</p>
                    <p className="text-xs text-muted-foreground">{formatRelativeTime(tx.created_at)}</p>
                  </div>
                  <span className={`text-sm font-medium ${tx.direction === 'in' ? 'text-green-500' : ''}`}>
                    {tx.direction === 'in' ? '+' : '-'}{formatCurrency(tx.amount).replace('₦', '₦')}
                  </span>
                </Link>
              );
            })}
            
            {recentTransactions.length === 0 && (
              <div className="text-center py-6 text-muted-foreground text-sm">
                No recent transactions
              </div>
            )}
          </div>
        </div>

        {/* Notifications Preview */}
        <div className="p-6 border-t border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Notifications</h3>
            <Link to="/notifications" className="text-xs text-primary hover:underline flex items-center gap-1">
              View All <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {notifications.slice(0, 3).map((notif) => (
              <div
                key={notif.notification_id}
                className={`p-3 rounded-xl text-sm ${notif.read ? 'bg-white/5' : 'bg-primary/10 border border-primary/20'}`}
              >
                <p className="font-medium truncate">{notif.title}</p>
                <p className="text-xs text-muted-foreground truncate">{notif.body}</p>
              </div>
            ))}
            
            {notifications.length === 0 && (
              <div className="text-center py-4 text-muted-foreground text-sm">
                No notifications
              </div>
            )}
          </div>
        </div>
      </aside>
      {/* Floating View Toggle */}
      <ViewToggleFAB viewMode={viewMode} setViewMode={setViewMode} />
    </div>
  );
};

const ViewToggleFAB = ({ viewMode, setViewMode }) => {
  const [open, setOpen] = useState(false);
  const modes = [
    { key: 'auto', icon: LayoutGrid, label: 'Auto' },
    { key: 'mobile', icon: Smartphone, label: 'Mobile' },
    { key: 'desktop', icon: Monitor, label: 'Desktop' },
  ];

  return (
    <div className="fixed bottom-6 right-6 z-[60]" data-testid="view-toggle-fab">
      {open && (
        <div className="absolute bottom-16 right-0 bg-card border border-white/10 rounded-xl p-2 shadow-2xl shadow-black/50 min-w-[140px] fade-in">
          {modes.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => { setViewMode(key); setOpen(false); }}
              className={`flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-sm transition-colors ${
                viewMode === key ? 'bg-primary text-white' : 'text-muted-foreground hover:text-white hover:bg-white/5'
              }`}
              data-testid={`view-toggle-${key}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-2xl shadow-black/50 transition-all active:scale-95 ${
          open ? 'bg-white/10 border border-white/20' : 'bg-primary glow-primary'
        }`}
        data-testid="view-toggle-btn"
      >
        {viewMode === 'mobile' ? <Smartphone className="w-5 h-5 text-white" /> :
         viewMode === 'desktop' ? <Monitor className="w-5 h-5 text-white" /> :
         <LayoutGrid className="w-5 h-5 text-white" />}
      </button>
    </div>
  );
};

export default AppLayout;
